import { Collection, collect } from 'collect.js';
import ReduxActions from '../../store/ReduxActions';
import {
  CompanyDetailsInterface,
  GeolocationInterface,
  GoogleAddressParamsInterface,
  GoogleLocationInterface,
} from '../exports/Interfaces';
import AddressHelpers from '../helpers/AddressHelpers';
import { loadGoogleMaps } from '../helpers/GoogleMapsLoader';

export class GooglePlaceResource {
  private static readonly AUTOCOMPLETE_BIAS_RADIUS_KM = 30;

  private data: google.maps.places.PlaceResult;
  private addressComponents: Collection<google.maps.GeocoderAddressComponent>;
  private company: CompanyDetailsInterface | null;

  constructor(data?: google.maps.places.PlaceResult | null, company?: CompanyDetailsInterface) {
    if (data) {
      this.data = data;
      this.addressComponents = collect(data.address_components);
    }

    if (company) {
      this.company = company;
    } else {
      this.company = this.getCompany();
    }
  }

  public getStreetName(): string {
    return this.addressComponents.first((component) => component.types.includes('route'))?.long_name ?? '';
  }

  public getStreetNumber(): string {
    return this.addressComponents.first((component) => component.types.includes('street_number'))?.long_name ?? '';
  }

  public getPostalCode(): string {
    return (
      this.addressComponents.first(
        (component) => component.types.includes('postal_code') && !component.types.includes('postal_code_prefix'),
      )?.long_name ?? ''
    );
  }

  public getCity(): string {
    return (
      this.addressComponents.first(
        (component) => component.types.includes('locality'),
        this.addressComponents.first((component) => component.types.includes('postal_town')),
      )?.long_name ?? ''
    );
  }

  public getState(): null | string {
    return (
      this.addressComponents.first((component) => component.types.includes('administrative_area_level_1'))?.long_name ??
      null
    );
  }

  public getCountry(): null | string {
    return this.addressComponents.first((component) => component.types.includes('country'))?.short_name ?? null;
  }

  public formatAddress(): string {
    return AddressHelpers.format({
      street_name: this.getStreetName(),
      street_number: this.getStreetNumber(),
      city: this.getCity(),
      zip_code: this.getPostalCode(),
      format: this.company?.region?.address_format,
    });
  }

  public getLat(): number {
    return this.getCoordinates()?.lat();
  }

  public getLng(): number {
    return this.getCoordinates()?.lng();
  }

  public getCoordinates(): google.maps.LatLng {
    return this.data?.geometry?.location;
  }

  public async getLocation(data: GoogleAddressParamsInterface): Promise<GoogleLocationInterface<google.maps.LatLng>> {
    try {
      // Maps loads on demand, so the first geocode of a visit may be what triggers it.
      await loadGoogleMaps();

      const geocoder = new google.maps.Geocoder();

      const { results } = await geocoder.geocode({
        address: AddressHelpers.format({
          street_name: data.streetName,
          street_number: data.streetNumber,
          city: data.city,
          state: data.state,
          zip_code: data.zip,
          format: this.company?.region?.address_format,
        }),
        // Without this Google may match a same-named street anywhere in the world — even with a
        // correct zip in the free-text address (e.g. Birmingham Road B72 -> the one in B43).
        ...(data.country ? { componentRestrictions: { country: data.country } } : {}),
        ...(data.bounds ? { bounds: data.bounds } : {}),
      });

      const result = GooglePlaceResource.filterResult(results);
      const location = result?.geometry.location;
      const resolved = result ? new GooglePlaceResource(result as google.maps.places.PlaceResult, this.company) : null;

      return {
        success: !!location,
        location,
        country: resolved?.getCountry() ?? null,
        postalCode: resolved?.getPostalCode() || null,
      };
    } catch (e) {
      console.error('Failed to find by address', e);

      return {
        success: false,
        location: null,
      };
    }
  }

  /**
   * Autocomplete used to be locked to the company's country, which hid the other side of the
   * border from restaurants that deliver across it (a DE outlet on the Salzach reaching AT).
   * Biasing towards the outlets instead keeps nearby results on top without excluding a
   * neighbouring country; the delivery zones still decide what is deliverable.
   */
  public static autocompleteBounds(
    points: { lat?: null | number; lng?: null | number }[],
    radiusKm: number = GooglePlaceResource.AUTOCOMPLETE_BIAS_RADIUS_KM,
  ): google.maps.LatLngBoundsLiteral | null {
    const located = collect(points).filter((point) => Number.isFinite(point?.lat) && Number.isFinite(point?.lng));

    if (located.isEmpty()) {
      return null;
    }

    const latPadding = radiusKm / 111;
    const north = Math.min((located.max('lat') as number) + latPadding, 90);
    const south = Math.max((located.min('lat') as number) - latPadding, -90);
    const widestLat = Math.max(Math.abs(north), Math.abs(south));
    const lngPadding = radiusKm / (111 * Math.max(Math.cos((widestLat * Math.PI) / 180), 0.01));

    return {
      north,
      south,
      east: Math.min((located.max('lng') as number) + lngPadding, 180),
      west: Math.max((located.min('lng') as number) - lngPadding, -180),
    };
  }

  public static async getPlaceFromCoordinates(
    coordinates: GeolocationInterface,
  ): Promise<GoogleLocationInterface<google.maps.GeocoderResult>> {
    try {
      await loadGoogleMaps();

      const geocoder = new google.maps.Geocoder();

      const { results } = await geocoder.geocode({
        location: coordinates,
      });

      const location = GooglePlaceResource.filterResult(results);

      return {
        success: !!location,
        location,
      };
    } catch (e) {
      console.error('Failed to find location by coordinates', e);

      return {
        success: false,
        location: null,
      };
    }
  }

  public calculateDistance(coordinates?: GeolocationInterface): number {
    const location = this.getCoordinates();

    if (!location || !coordinates || !location.lat || !location.lng || !coordinates.lng || !coordinates.lng) {
      return null;
    }

    try {
      return google.maps.geometry.spherical.computeDistanceBetween(this.getCoordinates(), coordinates);
    } catch (e) {
      console.error('Failed to calculate distance between 2 coordinates', e);

      return null;
    }
  }

  private getCompany(): CompanyDetailsInterface {
    if (this.company) {
      return this.company;
    }

    return ReduxActions.getStore()?.initialData?.company;
  }

  private static filterResult(results: google.maps.GeocoderResult[]): google.maps.GeocoderResult {
    return collect(results).first(
      (item) =>
        item.types.includes('street_address') ||
        item.types.includes('street_number') ||
        item.types.includes('neighborhood') ||
        item.types.includes('premise') ||
        item.types.includes('subpremise') ||
        item.types.includes('establishment'),
    );
  }
}
